/**
 * HomeScene — empty-document landing page redesign.
 *
 * Replaces the idle onboarding checklist for blank Word documents with a
 * hero card (greeting, static 3-step strip, "+ New document" CTA) and a
 * list of the user's drafted documents. Clicking a document triggers the
 * same load-into-Word flow as DraftsPanel's RunRow.handleLoad, so the
 * document content is hydrated and the shell exits idle to show the main UI.
 *
 * Spec: addin/src/variant-c/hooks/landingpage.txt
 * Data fetching mirrors DraftsPanel.tsx (listUserRuns, listUserActiveRuns,
 * 15s polling paused on document.hidden).
 */

import * as React from "react";
import type { ActiveRunSummary, DocTypeConfig } from "../../api/client";
import {
  blobToBase64,
  fetchRunBundle,
  fetchRunDocument,
  getDocTypeConfig,
  getDocumentSession,
  listActiveRuns,
  listUserActiveRuns,
  listUserFolderDocuments,
  listUserRuns,
  renameRun,
  runDocumentUrl,
} from "../../api/client";
import { openExternal } from "../services/openExternal";
import type { Workspace } from "../../types";
import type { DocSessionContext } from "../../services/wordWriter";
import {
  hydrateLiveDocFromBase64,
  saveDocSelectionProps,
  saveDocumentId,
  saveWordHydrationMarker,
  saveWordInsertionMode,
  setActiveRunId,
} from "../../services/wordWriter";
import { loadCustomRunIfApplicable } from "../services/loadCustomRunIntoLiveDoc";
import { loadRunIntoLiveDoc } from "../services/loadRunIntoLiveDoc";
import { leafDocumentIdForDoc, scopedCatalog } from "../leafDocuments";
import { enqueueWordWrite } from "../../services/wordWriteQueue";
import { rememberRunRecoveryHint } from "../services/documentSessionService";
import { buildGreeting, mapRunsToHomeDocs, type HomeDoc } from "../homeSceneMapping";
import { HomeDocCard } from "./HomeDocCard";
import { buildLoadSteps, HomeLoadProgress } from "./HomeLoadProgress";
import { HomeMenu } from "./HomeMenu";
import { HomePromptDialog, type HomePromptRequest } from "./HomePromptDialog";
import { useFolders } from "../hooks/useFiling";
import type { SpineSection } from "../hooks/useVariantCRun";
import { BackLink, Button, Eyebrow, LinkButton, SearchField, Stepper } from "../../ds/components";

/** The pseudo-folder id for "Unfiled" — documents with no ``folder_id``. Not a
 *  real folder: never renamed, deleted, or sent to the server. */
const UNFILED = "__unfiled__";

const POLL_INTERVAL_MS = 15_000;

interface OfficeLikeError {
  code?: string;
  message?: string;
  debugInfo?: {
    code?: string;
    message?: string;
    errorLocation?: string;
    statements?: string;
  };
}

function asOfficeError(err: unknown): OfficeLikeError | null {
  if (err && typeof err === "object" && ("debugInfo" in err || "code" in err)) {
    return err as OfficeLikeError;
  }
  return null;
}

function logLoadError(op: string, err: unknown): void {
  const oe = asOfficeError(err);
  if (oe) {
    console.error(`[HomeScene/Load] Office error in ${op}`, {
      code: oe.code,
      message: oe.message,
      errorLocation: oe.debugInfo?.errorLocation,
      debugInfo: oe.debugInfo,
    });
  } else {
    console.error(`[HomeScene/Load] error in ${op}`, err);
  }
}

function loadFailureMessage(err: unknown, prefix = "Load failed"): string {
  const oe = asOfficeError(err);
  const code = oe?.code ?? oe?.debugInfo?.code;
  if (code === "ItemNotFound" || code === "GeneralException") {
    return `${prefix}: couldn't rebuild this draft in the document. Try again.`;
  }
  const detail = err instanceof Error ? err.message : String(err);
  return `${prefix}: ${detail}`;
}

/**
 * The doctype config to load ``backendDocType`` with: the open one only when it
 * IS that doctype, else a fresh fetch. Home lists every project, so a Load can
 * open a 510(k) while the shell still holds CMC-BIO — reusing the open config
 * then built the loaded run's spine from the wrong template (0/0 drafted).
 */
export async function configForLoad(
  openConfig: DocTypeConfig | null | undefined,
  backendDocType: string,
): Promise<DocTypeConfig> {
  if (openConfig && (!backendDocType || openConfig.document_type === backendDocType)) return openConfig;
  return getDocTypeConfig(backendDocType);
}

export interface RunLoadContext {
  libraryId: string | null;
  libraryName: string | null;
  workspaceId: string | null;
  docType: string;
  /** Submission mode of the loaded run. ``"custom"`` (build-on-existing-doc)
   *  with an empty ``libraryId`` means the writer intentionally skipped the
   *  source library — ``handleRunLoaded`` must treat that as "no library"
   *  rather than restoring a stale one from the Word custom props. */
  submissionMode?: string | null;
  /** Client-side custom spine built from the imported document's own heading
   *  outline. Used as the authoritative spine when ``submissionMode==="custom"``
   *  and the server snapshot doesn't come back tagged custom (cold start /
   *  dropped field / deploy lag) — so the spine renders the writer's headings
   *  (green where content exists) instead of falling back to the doctype
   *  template skeleton. Only ever set by the custom-import flow. */
  customSpineFallback?: SpineSection[];
  /** The eCTD leaf the LOADED document is (its session's `leaf_document_id`),
   *  or null when it is not one. `undefined` = not looked up — the shell then
   *  keeps the leaf it already has. Lets the shell re-scope the spine to the
   *  document the Load just put in Word, not the one open before it. */
  leafDocumentId?: string | null;
  /** True when this Load rebuilt the document body from the server (not a
   *  Join-live onto a still-drafting run) — the shell then hands the document
   *  to the auto-save watcher once it confirms the run is finished. */
  bodyLoaded?: boolean;
}

interface HomeSceneProps {
  userIdentifier: string | null;
  docConfig?: DocTypeConfig | null;
  /** Drafting/context workspace — stamped onto NEW documents created via the
   *  "＋ New document" CTA. NOT used to scope the run list (see
   *  ``scopeWorkspaceId``); conflating the two would create new drafts in the
   *  wrong workspace when the writer is merely browsing another workspace. */
  workspaceId?: string | null;
  /** All workspaces the user can browse — populates the run-scope dropdown.
   *  Empty/undefined hides the dropdown. */
  workspaces?: Workspace[];
  /** Which workspace's runs to list. ``null`` = All workspaces (fetch omits
   *  ``workspace_id`` → every run the user owns). */
  scopeWorkspaceId?: string | null;
  /** The ``doc_id`` bound to the Word document currently open. When one of the
   *  listed runs targets it, that card is marked ``current`` (floated to the
   *  top as a highlighted "Reconnect" card). ``null`` when no doc is bound. */
  currentDocId?: string | null;
  /** Invoked when the writer changes the scope dropdown. ``null`` = All. */
  onScopeChange?: (workspaceId: string | null) => void;
  onBeforeLoad?: () => void;
  onRunLoaded?: (runId: string, config: DocTypeConfig, context?: RunLoadContext) => void | Promise<void>;
  /** Start the new-document flow. ``folderId`` (when the writer picked one in
   *  the creation dialog) is the folder the new document should be filed in;
   *  null/undefined = unfiled. */
  onNewDoc?: (folderId?: string | null) => void;
  /** The open Word document already holds text that is not an Asthra draft
   *  (e.g. the "Try on Word" starter document). Adds a one-line note that the
   *  first draft replaces it. */
  replacesExistingText?: boolean;
  refreshSignal?: number;
  /** Drafts-tab mode. When true, the scene lists EVERY completed run (the
   *  cross-run revision history) instead of one card per document, and each
   *  card gets a ⋯ overflow menu with "Open in Word" and "Download bundle".
   *  Off (the default) for the empty-doc Home, which stays Load-only. */
  enableRunActions?: boolean;
  /** Library names the shell already holds, keyed by library id — the card
   *  sub-line's fallback when a run row carries no ``library_name`` (never
   *  the project name). */
  libraryNameById?: Readonly<Record<string, string>> | ReadonlyMap<string, string> | null;
  /** Home's "See all drafts" link: switch to the Drafts tab. Hidden when absent. */
  onSeeAllDrafts?: () => void;
  /** Drafts list opened from Home: shows a back link to Home. */
  onBack?: () => void;
}

/** Trigger a browser download for a Blob (audit bundle .zip). Mirrors the
 *  helper the legacy DraftsPanel used. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the download a tick to start before revoking — some browsers race
  // the revoke against the download initiation.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The four new-document steps the Home card previews (board "02 Home"). */
const HOME_STEPS = ["Document type", "Region", "Sources", "Sections"] as const;


export function HomeScene({
  userIdentifier,
  docConfig,
  workspaceId,
  workspaces = [],
  scopeWorkspaceId = null,
  currentDocId = null,
  onScopeChange,
  onBeforeLoad,
  onRunLoaded,
  onNewDoc,
  replacesExistingText = false,
  refreshSignal,
  enableRunActions = false,
  libraryNameById = null,
  onSeeAllDrafts,
  onBack,
}: HomeSceneProps): React.ReactElement {
  // Read at map time so a fresh lookup object doesn't re-trigger the fetch.
  const libraryNameByIdRef = React.useRef(libraryNameById);
  libraryNameByIdRef.current = libraryNameById;
  const [homeDocs, setHomeDocs] = React.useState<HomeDoc[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Which folder is open (null = the top-level folder grid + unfiled docs). A
  // folder id drills in and scopes the fetch to that folder's documents, so a
  // drilled-in view is not limited to the top-level recent window. Declared
  // before ``refresh`` so the fetch can read it.
  const [openFolder, setOpenFolder] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [sort, setSort] = React.useState<"latest" | "name">("latest");
  // Which run's overflow action is in flight, keyed by runId, so only the
  // acting card shows "Opening…" / "Zipping…".
  const [menuBusy, setMenuBusy] = React.useState<{ runId: string; action: "open" | "bundle" } | null>(null);
  // The draft currently being loaded into Word (null when idle). While
  // set, the doc list is replaced by <HomeLoadProgress>; ``loadStepIndex``
  // is the active setup step and ``loadFailed`` freezes the stepper on
  // the failed step so the writer can read the error and retry.
  const [loadingDoc, setLoadingDoc] = React.useState<HomeDoc | null>(null);
  const [loadStepIndex, setLoadStepIndex] = React.useState(-1);
  // Per-section fill progress for the "Rebuilding in Word" step — the
  // scaffold-then-fill Load path (loadRunIntoLiveDoc) emits one tick per
  // section so the card shows ``x / total`` advancing instead of freezing on
  // a large (e.g. 96-section) doc. Null while no fill is in flight.
  const [loadProgress, setLoadProgress] = React.useState<{ current: number; total: number } | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const loadingDocId = loadingDoc?.runId ?? null;
  // Every folder's filed documents across ALL completed runs (server-side), so
  // a folder card's count isn't capped by the top-level recent-runs window.
  const [folderDocIds, setFolderDocIds] = React.useState<Record<string, string[]>>({});
  const isFetchingRef = React.useRef(false);

  const refresh = React.useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Completed runs are scoped to the selected workspace (``scopeWorkspaceId``;
      // null → all workspaces).
      //
      // Live runs come from TWO feeds, merged (deduped by run_id downstream):
      //   1. /runs/user/active — the writer's OWN in-progress runs, unscoped,
      //      so their actively-streaming draft is never hidden by the dropdown.
      //   2. /runs/active?workspace_id — every in-progress run in the selected
      //      workspace, CROSS-USER, so a teammate's live run in this workspace
      //      is attachable too. Only fetched when a workspace is selected
      //      (``/runs/active`` requires a scope; the "All workspaces" view has
      //      none to pass, so it falls back to the user feed alone).
      // When drilled into a folder, scope the completed feed to that folder so
      // its documents show even if they're outside the top-level recent window.
      // At the top level (null) fetch unscoped and group client-side. UNFILED
      // never reaches here — it isn't an ``openFolder`` value in this UI.
      const completedFolderScope = openFolder && openFolder !== UNFILED ? openFolder : null;
      // A folder view lists ALL its documents, so ask for a wider page than the
      // top-level recent window.
      const completedLimit = completedFolderScope ? 200 : 20;
      const [completedResp, userActiveResp, wsActiveResp, folderDocsResp] = await Promise.all([
        listUserRuns(completedLimit, scopeWorkspaceId, completedFolderScope).catch((err: unknown) => {
          console.warn("[HomeScene] listUserRuns failed:", err);
          return { scope: "user" as const, count: 0, documents: [] };
        }),
        listUserActiveRuns(20).catch((err: unknown) => {
          console.warn("[HomeScene] listUserActiveRuns failed:", err);
          return { scope: "user" as const, count: 0, runs: [] as ActiveRunSummary[] };
        }),
        scopeWorkspaceId
          ? listActiveRuns({ workspaceId: scopeWorkspaceId, limit: 20 }).catch((err: unknown) => {
              console.warn("[HomeScene] listActiveRuns(workspace) failed:", err);
              return { scope: "workspace_id" as const, count: 0, runs: [] as ActiveRunSummary[] };
            })
          : Promise.resolve({ scope: "workspace_id" as const, count: 0, runs: [] as ActiveRunSummary[] }),
        listUserFolderDocuments(scopeWorkspaceId).catch((err: unknown) => {
          console.warn("[HomeScene] listUserFolderDocuments failed:", err);
          return null;
        }),
      ]);
      if (folderDocsResp) setFolderDocIds(folderDocsResp.folders ?? {});

      // Own runs first so a same-run_id collision keeps the user's own row.
      const mergedActive = [...(userActiveResp.runs ?? []), ...(wsActiveResp.runs ?? [])];

      // One card per document on both Home and the Drafts tab (the latest run
      // represents it), so a folder box's document count matches the cards
      // inside it. ``enableRunActions`` only adds the per-card ⋯ actions.
      const mapped = mapRunsToHomeDocs(
        completedResp.documents ?? [],
        mergedActive,
        currentDocId,
        userIdentifier,
        false,
        libraryNameByIdRef.current,
      );
      setHomeDocs(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setHomeDocs([]);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [scopeWorkspaceId, currentDocId, userIdentifier, openFolder]);

  // --- Folders ------------------------------------------------------------
  // The workspaces whose folders this view lists: the scoped one, else every
  // workspace the writer can see, else the open document's workspace. A new
  // folder lands in the first of these.
  const folderWorkspaceIds = React.useMemo(() => {
    if (scopeWorkspaceId) return [scopeWorkspaceId];
    const ids = workspaces.map((w) => w.id).filter((id): id is string => Boolean(id));
    if (ids.length > 0) return ids;
    return workspaceId ? [workspaceId] : [];
  }, [scopeWorkspaceId, workspaces, workspaceId]);

  const {
    folders,
    reload: reloadFolders,
    create: createFolder,
    rename: renameFolder,
    remove: removeFolder,
    moveDocument,
  } = useFolders(folderWorkspaceIds);

  // A single name/choice dialog, with the action to run on its value.
  const [prompt, setPrompt] = React.useState<{
    request: HomePromptRequest;
    onValue: (value: string) => void | Promise<void>;
  } | null>(null);

  // Every document carries the folder it is filed in (``doc.folderId``). Group
  // the fetched cards by it so a folder box knows its count and the open view
  // shows only its documents.
  const docsByFolder = React.useMemo(() => {
    const map = new Map<string, HomeDoc[]>();
    for (const doc of homeDocs) {
      const key = doc.folderId ?? UNFILED;
      const list = map.get(key);
      if (list) list.push(doc);
      else map.set(key, [doc]);
    }
    return map;
  }, [homeDocs]);

  // A folder's document count: its documents across all completed runs
  // (server) plus any loaded card in it — e.g. a live doc with no completed run
  // yet — each document counted once.
  const folderDocCount = React.useCallback(
    (folderId: string): number => {
      const ids = new Set(folderDocIds[folderId] ?? []);
      let extra = 0;
      for (const doc of docsByFolder.get(folderId) ?? []) {
        if (doc.docId) ids.add(doc.docId);
        else extra += 1;
      }
      return ids.size + extra;
    },
    [folderDocIds, docsByFolder],
  );

  // Top level is "Recent": every fetched document, filed or not (tagged with
  // its folder on the card), so the doc open in Word is never hidden just
  // because it is filed. Inside a folder: only that folder's documents.
  const visibleDocs = React.useMemo(() => {
    if (openFolder === null) return homeDocs;
    return docsByFolder.get(openFolder) ?? [];
  }, [openFolder, homeDocs, docsByFolder]);

  const folderLabelById = React.useMemo(
    () => new Map((folders ?? []).map((f) => [f.id, f.label] as const)),
    [folders],
  );

  // A folder mutation or a move can change which folder a card belongs to, so
  // re-fetch the runs (folderId is joined server-side) as well as the folders.
  const refreshAll = React.useCallback(() => {
    void refresh();
    reloadFolders();
  }, [refresh, reloadFolders]);

  const askNewFolder = React.useCallback(
    (then?: (folderId: string) => void | Promise<void>) => {
      setPrompt({
        request: {
          kind: "name",
          title: "New folder",
          label: "Group related documents, for example one folder per study or product.",
          placeholder: "e.g. SNG-208 Phase 1",
          confirm: "Create folder",
        },
        onValue: async (name) => {
          const made = await createFolder(name);
          refreshAll();
          if (then) await then(made.id);
        },
      });
    },
    [createFolder, refreshAll],
  );

  const askRenameFolder = React.useCallback(
    (folderId: string, label: string) => {
      setPrompt({
        request: {
          kind: "name",
          title: "Rename folder",
          label: "Folder name",
          eyebrow: label,
          initial: label,
          confirm: "Save",
        },
        onValue: async (name) => {
          await renameFolder(folderId, name);
          refreshAll();
        },
      });
    },
    [renameFolder, refreshAll],
  );

  const handleDeleteFolder = React.useCallback(
    async (folderId: string) => {
      await removeFolder(folderId);
      setOpenFolder((open) => (open === folderId ? null : open));
      refreshAll();
    },
    [removeFolder, refreshAll],
  );

  // File one card into a folder — strictly per-document. Moving a card sets
  // ``folder_id`` on THAT document only (leaf or not); nothing else moves. A
  // leaf is never re-parented through its submission, which would sweep every
  // sibling doc and the live run into the folder.
  const fileCard = React.useCallback(
    async (doc: HomeDoc, folderId: string | null) => {
      await moveDocument(doc.docId, folderId);
      refreshAll();
    },
    [moveDocument, refreshAll],
  );

  const handleMoveToFolder = React.useCallback(
    (doc: HomeDoc) => {
      const options = [
        { value: UNFILED, label: "Unfiled", detail: "Not in a folder" },
        ...(folders ?? []).map((f) => ({ value: f.id, label: f.label })),
        { value: "__new__", label: "New folder…" },
      ];
      setPrompt({
        request: {
          kind: "choice",
          title: "Move to folder",
          eyebrow: doc.title,
          label: "Choose where this document should live.",
          options,
          initial: doc.folderId ?? UNFILED,
          confirm: "Move",
          createValue: "__new__",
        },
        onValue: async (value) => {
          if (value === "__new__") {
            askNewFolder((folderId) => fileCard(doc, folderId));
            return;
          }
          await fileCard(doc, value === UNFILED ? null : value);
        },
      });
    },
    [folders, fileCard, askNewFolder],
  );

  const openFolderLabel = React.useMemo(() => {
    if (openFolder === null) return null;
    if (openFolder === UNFILED) return "Unfiled";
    return (folders ?? []).find((f) => f.id === openFolder)?.label ?? "Folder";
  }, [openFolder, folders]);

  // "＋ New document": start the flow straight away. The name and folder are
  // set above Start on the section picker, preselected to the folder open here.
  const handleNewDoc = React.useCallback(() => {
    onNewDoc?.(openFolder && openFolder !== UNFILED ? openFolder : null);
  }, [onNewDoc, openFolder]);

  // Drafts-tab overflow actions. Open pops a fresh Word window on the run's
  // canonical .docx; Bundle downloads the audit .zip. Both are best-effort and
  // surface failures through the existing error banner.
  const handleOpenExternal = React.useCallback((doc: HomeDoc) => {
    try {
      openExternal(runDocumentUrl(doc.runId, false));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleDownloadBundle = React.useCallback(async (doc: HomeDoc) => {
    setMenuBusy({ runId: doc.runId, action: "bundle" });
    try {
      const blob = await fetchRunBundle(doc.runId, { includeProvenance: false });
      downloadBlob(blob, `run-${doc.runId}-bundle.zip`);
    } catch (err) {
      setError(`Bundle download failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setMenuBusy(null);
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const refreshSignalSeenRef = React.useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshSignal is the external re-fetch trigger
  React.useEffect(() => {
    if (!refreshSignalSeenRef.current) {
      refreshSignalSeenRef.current = true;
      return;
    }
    void refresh();
  }, [refreshSignal, refresh]);

  // Dismiss the progress card after a failed load and return to the list.
  const handleDismissLoad = React.useCallback(() => {
    setLoadingDoc(null);
    setLoadStepIndex(-1);
    setLoadProgress(null);
    setLoadFailed(false);
    setError(null);
  }, []);

  const handleLoadDoc = React.useCallback(
    async (doc: HomeDoc) => {
      // Step 0 — "Preparing". Clearing loadFailed/error resets any prior
      // failed attempt so a retry starts from a clean stepper.
      setLoadingDoc(doc);
      setLoadStepIndex(0);
      setLoadProgress(null);
      setLoadFailed(false);
      setError(null);
      onBeforeLoad?.();

      try {
        // Resolve the config by the BACKEND doc_type (e.g.
        // "CMC_ANDA_SMALL_MOLECULE"), never the display code
        // (``doc.doctype`` = "CMC-ANDA") — the latter 404s
        // ``/doc-types/{id}``. Mirrors DraftsPanel's RunRow, which passes
        // ``run.doc_type``. An open config is reused only for the same doctype.
        const fullCfg = await configForLoad(docConfig, doc.backendDocType);
        // Narrow to the eCTD leaf the LOADED document is, so the Word rebuild
        // carries only that leaf's headings. Single-document doctypes (and
        // documents with no stored leaf) come back unchanged.
        const leafDocumentId = await leafDocumentIdForDoc(doc.docId, getDocumentSession);
        const cfg = scopedCatalog(fullCfg, leafDocumentId) ?? fullCfg;

        // Steps 1→2 — prefer the canonical server-rendered .docx, whole-file.
        // fetchRunDocument (/runs/{id}/document) rebuilds the whole doc from the
        // Mongo SSOT with every section body already placed under its heading;
        // hydrateLiveDocFromBase64 inserts it in ~ONE Office.js operation, with
        // no add-in-side section-number matching. This is what the spine "Load
        // doc" (handleLoadDraftedDocument) and the legacy DraftsPanel already do,
        // and it is MUCH faster than the per-section replay: for a 91-section
        // protocol the scaffold-then-fill path does ~91 serialized Range.search +
        // insert + sync + citation round-trips, which dominate wall-clock on Word.
        //
        // The per-section scaffold-then-fill (loadRunIntoLiveDoc) is kept ONLY as
        // a fallback — for a 404 (no exported bundle yet, e.g. a still-running
        // run) and for a mid-insert whole-file failure (Word-for-the-web's
        // single-insert ceiling on very large docs, the reason this path was
        // originally per-section only). It reads /all-sections and wraps citations
        // per section, so the fallback body is byte-equivalent. Each emits a
        // progress tick to advance the card to "Rebuilding in Word (x/total)".
        // Every body rebuild below goes through the single Word-write queue so
        // it can't interleave with a section re-projection still rendering.
        const scaffoldFill = () =>
          enqueueWordWrite(() =>
            loadRunIntoLiveDoc(doc.runId, cfg, {
              onProgress: ({ completed, total }) => {
                setLoadStepIndex(2);
                setLoadProgress({ current: completed, total });
              },
            }),
          );

        setLoadStepIndex(1); // Downloading your draft
        // Custom (build-on-existing-document) run → dedicated per-section Load;
        // the whole-file hydrate can't place its CUSTOM-N sections (blank doc)
        // and the template scaffoldFill would orphan them. Returns null for a
        // normal template run, which falls through to the hydrate path below.
        const customResult = await enqueueWordWrite(() =>
          loadCustomRunIfApplicable(doc.runId, {
            onProgress: ({ completed, total }) => {
              setLoadStepIndex(2);
              setLoadProgress({ current: completed, total });
            },
          }),
        );
        if (!customResult) {
          const blob = await fetchRunDocument(doc.runId, { includeProvenance: false });
          if (blob) {
            const base64 = await blobToBase64(blob);
            try {
              setLoadStepIndex(2); // Rebuilding in Word (whole-file hydrate)
              await enqueueWordWrite(() =>
                hydrateLiveDocFromBase64(base64, {
                  styleSpec: cfg.live_doc_style_spec,
                  skeleton: cfg.skeleton,
                  headingNumbering: cfg.heading_numbering,
                }),
              );
            } catch (hydrateErr) {
              // The whole-file hydrate clears the body BEFORE inserting, so a
              // mid-insert failure leaves an EMPTY doc — the worst outcome. Rebuild
              // per-section from the backend SSOT so the writer still gets content.
              // Mirrors DraftsPanel.handleLoad / the spine's handleLoadDraftedDocument.
              logLoadError("handleLoadDoc.hydrate", hydrateErr);
              await scaffoldFill();
            }
          } else {
            // 404 — no exported bundle yet (still-running / paused run). Fall back
            // to per-section replay, which works from whatever /all-sections has.
            await scaffoldFill();
          }
        }

        // Step 3 — "Restoring session" (doc id, active run, markers, citations).
        setLoadStepIndex(3);
        try {
          if (doc.docId) {
            await saveDocumentId(doc.docId);
          }
          await setActiveRunId(doc.runId);
          await saveWordHydrationMarker({
            runId: doc.runId,
            snapshotVersion: null,
            snapshotTs: null,
          });
          await saveWordInsertionMode(cfg.generation?.insertion_mode === "section" ? "section" : "batch");

          if (doc.backendDocType && doc.backendDocType.length > 0) {
            // Persist the BACKEND doc_type (mirrors DraftsPanel) — the session
            // custom-props are read back to re-resolve the config, so storing
            // the display code "CMC-ANDA" would 404 on the next resolve.
            //
            // Use the run's REAL library id/name (carried on the HomeDoc from
            // the run list). Previously this stashed ``doc.docId`` as the
            // libraryId — a document id, not a library id — so the shell's
            // ``handleRunLoaded`` selection restore (which gates on
            // ``isValidLibraryId``) rejected it and both the doctype + library
            // chips stayed on their "pick a…" placeholders after Load.
            const sessionContext: DocSessionContext = {
              libraryId: doc.libraryId || "",
              libraryName: doc.libraryName || doc.libraryId || "",
              docCode: doc.backendDocType,
              docName: cfg.display_name || doc.backendDocType,
              backendDocType: doc.backendDocType,
              workspaceId: workspaceId ?? null,
            };
            await saveDocSelectionProps(sessionContext);
          }
        } catch (err) {
          logLoadError("postLoadBookkeeping", err);
        }

        const runLoadContext: RunLoadContext = {
          // The run's real library id/name — mirrors DraftsPanel.runLoadContext.
          // ``handleRunLoaded`` prefers this over the Word custom-prop round-trip
          // and only restores the doctype + library chips when the library id is
          // valid, so passing null here (the old behaviour) left them blank.
          libraryId: doc.libraryId,
          libraryName: doc.libraryName,
          workspaceId: workspaceId ?? null,
          // Backend doc_type (matches DraftsPanel) so ``handleRunLoaded`` restores
          // context against the real type, not the display code.
          docType: doc.backendDocType,
          leafDocumentId,
          bodyLoaded: true,
        };

        // Step 4 — "Loading the spine". When onRunLoaded resolves the shell
        // transitions out of idle and this scene unmounts into the ready
        // spine, so the stepper is torn down rather than showing a "done".
        setLoadStepIndex(4);
        await onRunLoaded?.(doc.runId, fullCfg, runLoadContext);
        // Success: clear the in-flight state. Usually a no-op because the
        // shell has already unmounted us, but harmless if it hasn't yet.
        setLoadingDoc(null);
        setLoadStepIndex(-1);
        setLoadProgress(null);
      } catch (err) {
        // Freeze the stepper on the failed step and surface the error in
        // the progress card (loadingDoc stays set → card stays mounted).
        logLoadError("handleLoadDoc", err);
        setError(loadFailureMessage(err));
        setLoadFailed(true);
      }
    },
    [docConfig, workspaceId, onRunLoaded, onBeforeLoad],
  );

  // Reconnect to an in-progress run. Unlike ``handleLoadDoc``, a live run has
  // no exported bundle yet, so we skip the whole-file download and paint the
  // sections drafted-so-far via the per-section path (``loadRunIntoLiveDoc``
  // reads DraftRunDoc.draft_full, written per-section as SSE fires). Once
  // ``onRunLoaded`` dispatches RUN_RESTORED, useVariantCRun re-subscribes to
  // /runs/{id}/events and streams the remaining sections into the spine live.
  // Mirrors DraftsPanel.handleJoinLive.
  const handleJoinLiveDoc = React.useCallback(
    async (doc: HomeDoc) => {
      // Step 0 "Preparing" → step 2 "Rebuilding in Word" (skip "Downloading",
      // there is no bundle to fetch for a live run).
      setLoadingDoc(doc);
      setLoadStepIndex(0);
      setLoadProgress(null);
      setLoadFailed(false);
      setError(null);
      onBeforeLoad?.();

      try {
        rememberRunRecoveryHint(doc.runId, "home.join-live");
        const fullCfg = await configForLoad(docConfig, doc.backendDocType);
        // Same leaf narrowing as handleLoadDoc — see there.
        const leafDocumentId = await leafDocumentIdForDoc(doc.docId, getDocumentSession);
        const cfg = scopedCatalog(fullCfg, leafDocumentId) ?? fullCfg;

        setLoadStepIndex(2);
        await enqueueWordWrite(() =>
          loadRunIntoLiveDoc(doc.runId, cfg, {
            onProgress: ({ completed, total }) => setLoadProgress({ current: completed, total }),
          }),
        );

        // Step 3 — "Restoring session". Best-effort: the sections are already
        // in the doc and SSE re-attach only needs onRunLoaded to fire, so a
        // custom-prop stamp failure must not abort the join.
        setLoadStepIndex(3);
        try {
          if (doc.docId) {
            await saveDocumentId(doc.docId);
          }
          await setActiveRunId(doc.runId);
          await saveWordHydrationMarker({ runId: doc.runId, snapshotVersion: null, snapshotTs: null });
          await saveWordInsertionMode(cfg.generation?.insertion_mode === "section" ? "section" : "batch");
          if (doc.backendDocType && doc.backendDocType.length > 0) {
            // The run's REAL library id/name, as in handleLoadDoc — ``doc.docId``
            // is a ``doc-…`` session id that ``isValidLibraryId`` rejects, so
            // stamping it here left the doctype + library chips blank after a
            // Join live and kept a stale library on the session.
            const sessionContext: DocSessionContext = {
              libraryId: doc.libraryId || "",
              libraryName: doc.libraryName || doc.libraryId || "",
              docCode: doc.backendDocType,
              docName: cfg.display_name || doc.backendDocType,
              backendDocType: doc.backendDocType,
              workspaceId: workspaceId ?? null,
            };
            await saveDocSelectionProps(sessionContext);
          }
        } catch (err) {
          logLoadError("handleJoinLiveDoc.postLoad", err);
        }

        const runLoadContext: RunLoadContext = {
          libraryId: doc.libraryId,
          libraryName: doc.libraryName,
          workspaceId: workspaceId ?? null,
          docType: doc.backendDocType,
          leafDocumentId,
        };

        // Step 4 — "Loading the spine". onRunLoaded dispatches RUN_RESTORED;
        // because the run is non-terminal the shell leaves terminalRunStatus
        // false and the SSE subscription re-opens, so the spine keeps filling.
        setLoadStepIndex(4);
        await onRunLoaded?.(doc.runId, fullCfg, runLoadContext);
        setLoadingDoc(null);
        setLoadStepIndex(-1);
        setLoadProgress(null);
      } catch (err) {
        logLoadError("handleJoinLiveDoc", err);
        setError(loadFailureMessage(err, "Reconnect failed"));
        setLoadFailed(true);
      }
    },
    [docConfig, workspaceId, onRunLoaded, onBeforeLoad],
  );

  // Route each card to the correct handler: a live run reconnects (join-live),
  // a completed run loads its exported bundle.
  const handleCardOpen = React.useCallback(
    (doc: HomeDoc) => (doc.live ? handleJoinLiveDoc(doc) : handleLoadDoc(doc)),
    [handleJoinLiveDoc, handleLoadDoc],
  );

  const handleRename = React.useCallback(
    async (doc: HomeDoc, name: string) => {
      try {
        await renameRun(doc.runId, name);
        await refresh();
      } catch (err) {
        console.warn("[HomeScene] renameRun failed:", err);
      }
    },
    [refresh],
  );

  const now = new Date();
  const greeting = buildGreeting(now, userIdentifier);
  const isDrafts = Boolean(enableRunActions);

  // Drafts tab: search and sort over the fetched list (board "38 Drafts").
  const listedDocs = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q
      ? visibleDocs.filter((d) =>
          [d.title, d.sub, d.doctype, d.libraryName ?? ""].some((t) => t.toLowerCase().includes(q)),
        )
      : visibleDocs;
    if (sort === "name") return [...matched].sort((a, b) => a.title.localeCompare(b.title));
    return matched;
  }, [visibleDocs, query, sort]);

  const docList = (docs: HomeDoc[]) => (
    <div className="as-stack-8">
      {docs.map((doc) => (
        <HomeDocCard
          key={doc.runId}
          doc={doc}
          onLoad={handleCardOpen}
          loading={loadingDocId === doc.runId}
          onOpenExternal={enableRunActions ? handleOpenExternal : undefined}
          onDownloadBundle={enableRunActions ? handleDownloadBundle : undefined}
          menuBusy={menuBusy?.runId === doc.runId ? menuBusy.action : null}
          onRename={handleRename}
          onMoveToFolder={folderWorkspaceIds.length > 0 ? handleMoveToFolder : undefined}
          folderLabel={openFolder === null && doc.folderId ? folderLabelById.get(doc.folderId) : null}
        />
      ))}
    </div>
  );

  const loadOrError = loadingDoc ? (
    // A draft is being opened: a step-by-step progress card replaces the
    // list until the shell swaps this scene for the Spine.
    <HomeLoadProgress
      docTitle={loadingDoc.title}
      steps={buildLoadSteps(loadStepIndex, loadProgress)}
      error={loadFailed ? error : null}
      onDismiss={loadFailed ? handleDismissLoad : undefined}
    />
  ) : null;

  const errorBanner = error && !loadingDoc ? (
    <div className="as-notice as-notice-danger" role="alert">
      <div>Couldn&apos;t load your documents: {error}</div>
    </div>
  ) : null;

  const emptyState =
    listedDocs.length === 0 && !isLoading ? (
      <p className="as-empty">
        {query.trim()
          ? "No drafts match that search."
          : openFolder === null
            ? "No documents yet. Start your first draft above."
            : "This folder has no documents yet."}
      </p>
    ) : null;

  const promptDialog = (
    <HomePromptDialog
      request={prompt?.request ?? null}
      onSubmit={(value) => {
        const pending = prompt;
        setPrompt(null);
        void pending?.onValue(value);
      }}
      onCancel={() => setPrompt(null)}
    />
  );

  if (isDrafts) {
    // Board "38 Drafts": title + New document, search, workspace + sort, cards.
    return (
      <div className="as-screen">
        <header className="as-drafts-head">
          {onBack ? <BackLink onClick={onBack}>Home</BackLink> : null}
          <div className="as-between">
            <h1 className="as-h1">Drafts</h1>
            <Button variant="primary" size="sm" icon="plus" onClick={handleNewDoc}>
              New document
            </Button>
          </div>
          <SearchField value={query} onChange={setQuery} placeholder="Search drafts, studies or libraries" />
          <div className="as-drafts-filters">
            {workspaces.length > 0 ? (
              <>
                <label htmlFor="drafts-ws">Workspace</label>
                <select
                  id="drafts-ws"
                  className="as-input as-select as-select-sm"
                  aria-label="Filter documents by workspace"
                  data-testid="home-workspace-scope"
                  value={scopeWorkspaceId ?? ""}
                  onChange={(e) => onScopeChange?.(e.target.value || null)}
                >
                  <option value="">All workspaces</option>
                  {workspaces.map((ws) => (
                    <option key={ws.id} value={ws.id}>
                      {ws.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <span style={{ flex: 1 }} />
            )}
            <select
              aria-label="Sort"
              className="as-input as-select as-select-sm as-select-fit"
              value={sort}
              onChange={(e) => setSort(e.target.value === "name" ? "name" : "latest")}
            >
              <option value="latest">Latest first</option>
              <option value="name">Name</option>
            </select>
          </div>
        </header>
        <div className="as-scroll as-sunken">
          <div className="as-drafts-list">
            {loadOrError ?? (
              <>
                {errorBanner}
                {isLoading && homeDocs.length === 0 ? <p className="as-empty">Loading your drafts…</p> : null}
                {emptyState}
                {docList(listedDocs)}
              </>
            )}
          </div>
        </div>
        {promptDialog}
      </div>
    );
  }

  // Board "02 Home": greeting card with the four setup steps, folders, recent.
  return (
    <div className="as-screen">
      <div className="as-scroll">
        <div className="as-home">
          <section className="as-home-hero">
            <h1 className="as-h1-lg">{greeting}</h1>
            <p className="as-p">Start a draft. You get a cited first draft and a QC report, usually within 1–2 hours.</p>
            <Stepper current={0} steps={HOME_STEPS} />
            <Button variant="primary" block icon="plus" onClick={handleNewDoc}>
              New document
            </Button>
            {replacesExistingText && (
              <p className="as-footer-note" data-testid="home-replaces-text">
                The text already in this Word document will be replaced by your draft.
              </p>
            )}
          </section>

          {loadOrError ?? (
            <>
              {errorBanner}
              {openFolder === null ? (
                <section className="as-stack-8">
                  <div className="as-between">
                    <Eyebrow>Folders</Eyebrow>
                    {folderWorkspaceIds.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="plus"
                        onClick={() => askNewFolder()}
                        data-testid="home-new-folder"
                      >
                        New folder
                      </Button>
                    )}
                  </div>
                  {(folders?.length ?? 0) > 0 ? (
                    <div className="as-folder-grid" data-testid="home-folder-grid">
                      {(folders ?? []).map((folder) => (
                        <div key={folder.id} className="as-folder">
                          <button
                            type="button"
                            className="as-folder-face"
                            onClick={() => setOpenFolder(folder.id)}
                            data-testid="home-folder-box"
                          >
                            <span className="as-folder-name">{folder.label}</span>
                            <span className="as-folder-meta">
                              {folderDocCount(folder.id)} document{folderDocCount(folder.id) === 1 ? "" : "s"}
                            </span>
                          </button>
                          <div className="as-folder-menu">
                            <HomeMenu
                              label={`Folder actions for ${folder.label}`}
                              items={[
                                { label: "Rename", onSelect: () => askRenameFolder(folder.id, folder.label) },
                                {
                                  label: "Delete",
                                  destructive: true,
                                  onSelect: () => void handleDeleteFolder(folder.id),
                                },
                              ]}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              ) : (
                <div className="as-between">
                  <BackLink onClick={() => setOpenFolder(null)}>All folders</BackLink>
                  <Eyebrow>{openFolderLabel}</Eyebrow>
                </div>
              )}

              <section className="as-stack-8">
                {openFolder === null ? (
                  <div className="as-between">
                    <Eyebrow>Recent</Eyebrow>
                    {onSeeAllDrafts ? <LinkButton onClick={onSeeAllDrafts}>See all drafts</LinkButton> : null}
                  </div>
                ) : null}
                {isLoading && homeDocs.length === 0 ? <p className="as-empty">Loading your documents…</p> : null}
                {emptyState}
                {docList(listedDocs)}
              </section>
            </>
          )}
        </div>
      </div>
      {promptDialog}
    </div>
  );
}
